import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Design.css';

const Design = () => {
  const navigate = useNavigate();
  const tshirtRef = useRef(null);
  const canvasRef = useRef(null);

  const handleMouseMove = (e, cardRef) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty('--mx', `${x}%`);
    card.style.setProperty('--my', `${y}%`);
  };

  const handleMouseLeave = (cardRef) => {
    const card = cardRef.current;
    if (!card) return;
    card.style.setProperty('--mx', '50%');
    card.style.setProperty('--my', '50%');
  };

  return (
    <div className="design-page">
      <div className="design-bg-grid" />
      <div className="design-bg-orb design-bg-orb--1" />
      <div className="design-bg-orb design-bg-orb--2" />

      <div className="design-hero">
        <div className="design-hero__eyebrow">Creative Studio</div>
        <h1 className="design-hero__title">
          What will you
          <br />
          <span className="design-hero__title--accent">design today?</span>
        </h1>
        <p className="design-hero__sub">
          Pick a surface. Make something yours.
        </p>
      </div>

      <div className="design-cards">
        {/* T-SHIRT CARD */}
        <div
          ref={tshirtRef}
          className="design-card design-card--tshirt"
          onMouseMove={(e) => handleMouseMove(e, tshirtRef)}
          onMouseLeave={() => handleMouseLeave(tshirtRef)}
          onClick={() => navigate('/design/tshirt')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/design/tshirt')}
          aria-label="Go to T-Shirt Designer"
        >
          <div className="design-card__glow" />
          <div className="design-card__inner">
            <div className="design-card__icon-wrap">
              <svg className="design-card__shirt-svg" viewBox="0 0 120 130" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M26 12 C36 4, 84 4, 94 12 L116 34 L98 40 L98 112 L22 112 L22 40 L4 34 Z"
                  fill="url(#shirtGrad)"
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="1.5"
                />
                <path d="M26 12 C36 4, 84 4, 94 12" stroke="rgba(255,255,255,0.1)" strokeWidth="3" fill="none" />
                <path d="M46 12 Q60 22 74 12" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" fill="none" />
                <rect x="40" y="52" width="40" height="3" rx="1.5" fill="rgba(255,255,255,0.25)" />
                <rect x="46" y="61" width="28" height="3" rx="1.5" fill="rgba(255,255,255,0.15)" />
                <rect x="42" y="70" width="36" height="3" rx="1.5" fill="rgba(255,255,255,0.10)" />
                <defs>
                  <linearGradient id="shirtGrad" x1="60" y1="0" x2="60" y2="130" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#7c6dfa" />
                    <stop offset="100%" stopColor="#4e3ec8" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="design-card__badge design-card__badge--live">Live</div>
            </div>

            <div className="design-card__content">
              <div className="design-card__label">Surface 01</div>
              <h2 className="design-card__title">T-Shirt<br />Designer</h2>
              <p className="design-card__desc">
                Upload your shirt, add text and graphics, pick colors — then export a print-ready PNG.
              </p>
              <ul className="design-card__features">
                <li><span className="design-card__feat-dot" />Custom shirt upload + color tinting</li>
                <li><span className="design-card__feat-dot" />Text with fonts, styles &amp; alignment</li>
                <li><span className="design-card__feat-dot" />8 preset graphics (star, crown, heart…)</li>
                <li><span className="design-card__feat-dot" />Try-on preview &amp; PNG export</li>
              </ul>
            </div>

            <div className="design-card__cta">
              <span>Open Studio</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* DIVIDER */}
        <div className="design-divider">
          <div className="design-divider__line" />
          <div className="design-divider__or">or</div>
          <div className="design-divider__line" />
        </div>

        {/* WALL CANVAS CARD */}
        <div
          ref={canvasRef}
          className="design-card design-card--canvas design-card--coming-soon"
          onMouseMove={(e) => handleMouseMove(e, canvasRef)}
          onMouseLeave={() => handleMouseLeave(canvasRef)}
          role="button"
          tabIndex={0}
          aria-label="Wall Canvas Designer — Coming Soon"
        >
          <div className="design-card__glow" />
          <div className="design-card__inner">
            <div className="design-card__icon-wrap">
              <svg className="design-card__canvas-svg" viewBox="0 0 120 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="8" width="104" height="80" rx="4" fill="url(#canvasGrad)" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
                <rect x="16" y="16" width="88" height="64" rx="2" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                <circle cx="36" cy="40" r="10" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                <path d="M24 68 L42 50 L56 60 L70 44 L96 68" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
                <path d="M60 88 L44 106" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round" />
                <path d="M60 88 L76 106" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round" />
                <path d="M60 88 L60 106" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" />
                <defs>
                  <linearGradient id="canvasGrad" x1="60" y1="0" x2="60" y2="110" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#2e2740" />
                    <stop offset="100%" stopColor="#1a1628" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="design-card__badge design-card__badge--soon">Soon</div>
            </div>

            <div className="design-card__content">
              <div className="design-card__label">Surface 02</div>
              <h2 className="design-card__title">Wall Canvas<br />Designer</h2>
              <p className="design-card__desc">
                Create stunning wall art with full canvas control — illustrations, patterns, and custom prints.
              </p>
              <ul className="design-card__features design-card__features--muted">
                <li><span className="design-card__feat-dot design-card__feat-dot--dim" />Freehand drawing &amp; shapes</li>
                <li><span className="design-card__feat-dot design-card__feat-dot--dim" />Background patterns &amp; gradients</li>
                <li><span className="design-card__feat-dot design-card__feat-dot--dim" />Multi-layer artwork support</li>
                <li><span className="design-card__feat-dot design-card__feat-dot--dim" />Print-size export (A2, A1, custom)</li>
              </ul>
            </div>

            <div className="design-card__cta design-card__cta--disabled">
              <span>Coming Soon</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Design;